import React from "react";
import { Box, Typography, styled } from "@mui/material";
import type { SimpleNamedItem } from "../../../types";

interface RouteCardProps {
  title: string;
  subtitle: string;
  items: SimpleNamedItem[];
  headerColor: string;
  iconBgColor: string;
  icon: React.ReactNode;
}

const CardContainer = styled(Box, {
  label: "RouteCardContainer",
})(() => ({
  width: 299,
  position: "relative",
  paddingTop: 40,
}));

const IconCircle = styled(Box, {
  label: "RouteCardIconCircle",
  shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor: string }>(({ bgColor }) => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: 72,
  height: 72,
  borderRadius: "50%",
  backgroundColor: bgColor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
}));

const Card = styled(Box, {
  label: "RouteCard",
})(() => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: 24,
  overflow: "hidden",
  height: 314,
}));

const CardHeader = styled(Box, {
  label: "RouteCardHeader",
  shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor: string }>(({ bgColor, theme }) => ({
  backgroundColor: bgColor,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  color: "#ffffff",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const CardBody = styled(Box, {
  label: "RouteCardBody",
})(({ theme }) => ({
  backgroundColor: "#ffffff",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  alignItems: "center",
  padding: theme.spacing(3),
  borderBottomLeftRadius: 24,
  borderBottomRightRadius: 24,
}));

const RouteItem = styled(Box, {
  label: "RouteItem",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  width: 251,
}));

const RouteItemIcon = styled("img", {
  label: "RouteItemIcon",
})(() => ({
  width: 34,
  height: 34,
  objectFit: "contain",
}));

export const RouteCard = ({
  title,
  subtitle,
  items,
  headerColor,
  iconBgColor,
  icon,
}: RouteCardProps) => {
  return (
    <CardContainer>
      <IconCircle bgColor={iconBgColor}>{icon}</IconCircle>

      <Card>
        <CardHeader bgColor={headerColor}>
          <Typography
            sx={{
              fontFamily: "Proxima Nova",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "36px",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Proxima Nova",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "18px",
            }}
          >
            {subtitle}
          </Typography>
        </CardHeader>

        <CardBody>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && (
                <Typography
                  sx={{
                    fontFamily: "Proxima Nova",
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#637381",
                    textAlign: "center",
                  }}
                >
                  Or
                </Typography>
              )}
              <RouteItem>
                {item.icon?.url && (
                  <RouteItemIcon src={item.icon.url} alt={item.name} />
                )}
                <Typography
                  sx={{
                    fontFamily: "Proxima Nova",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "28px",
                    color: "#1c252e",
                    textAlign: "center",
                  }}
                >
                  {item.name}
                </Typography>
              </RouteItem>
            </React.Fragment>
          ))}
        </CardBody>
      </Card>
    </CardContainer>
  );
};
